import { atom } from "jotai";
import { atomWithStorage, createJSONStorage } from "jotai/utils";
import { Mi1Engine } from "./engines/mi1Engine";
import { CogEngine } from "./engines/cogEngine";
import { Co1Engine } from "./engines/co1Engine";
import { Np1Engine } from "./engines/np1Engine";
import { DysphagiaEngine } from "./engines/dysphagiaEngine";
import { Mi2Engine } from "./engines/mi2Engine";
import { Br1Engine } from "./engines/br1Engine";
import { MciEngine } from "./engines/mciEngine";
import { TOKEN_INFO_SESSION_STORAGE_KEY } from "./constants";
import { AudioRecorder } from "./utils/audioRecorder/audioRecorder";
import workletUrl from "./assets/js/RecorderProcessor.js?url";
import { themeType } from "./types";
import { Sl1Engine } from "./engines/sl1Engine";
import { Co2Engine } from "./engines/co2Engine";
import { decodeJwt } from "./utils/decodeJwt";

// 規約同意情報型定義
type UserAgreements = Record<string, boolean>;

// 全ユーザーの同意情報を一つのオブジェクトで管理
export const allAgreementsAtom = atomWithStorage<UserAgreements>(
  "user-agreements",
  {}
);

const DETECT_VOLUME_LOCALSTORAGE_KEY = "detectVolume";
export const detectVolumeAtom = atomWithStorage(
  DETECT_VOLUME_LOCALSTORAGE_KEY,
  -1
);

export const detectedVolumeByPhraseVisualizerAtom = atom(false);

export const themeColorAtom = atomWithStorage<themeType>(
  "themeColor",
  "tateshina_light"
);

export const cognitiveEnginesAtom = atom([
  new CogEngine(),
  new Co1Engine(),
  new Np1Engine(),
]);

export const cogEnginesAtom = atom([
  new Mi1Engine(),
  new Mi2Engine(),
  new CogEngine(),
]);

export const co1EnginesAtom = atom([new Co1Engine()]);

export const np1EnginesAtom = atom([new Np1Engine()]);

export const mi1_Mi2EnginesAtom = atom([new Mi1Engine(), new Mi2Engine()]);

export const mi1EnginesAtom = atom([new Mi1Engine()]);

export const mi2EnginesAtom = atom([new Mi2Engine()]);

export const dysphagiaEnginesAtom = atom([new DysphagiaEngine()]);

export const br1EnginesAtom = atom([new Br1Engine()]);

export const uixEnginesAtom = atom([
  new Mi1Engine(),
  new Mi2Engine(),
  new Co1Engine(),
  new Br1Engine(),
]);

export const co1DemoEnginesAtom = atom([
  new Co1Engine({ _analysisType: "Co1Demo" }),
]);

export const sl1EnginesAtom = atom([new Sl1Engine({ _analysisType: "sl1" })]);

export const Mi2DemoEnginesAtom = atom([
  new Mi2Engine({ _analysisType: "Mi2Demo" }),
]);

export const Co2DemoEnginesAtom = atom([
  new Co2Engine({ _analysisType: "Co2Demo" }),
]);

export const tfhEnginesAtom = atom([
  new Mi2Engine({ _analysisType: "TFH" }),
  new Co2Engine({ _analysisType: "TFH" }),
]);

export const tokioDRSl1EngineAtom = atom([
  new Sl1Engine({ _analysisType: "TokioDRSl1" }),
]);
export const tokioDRCognitiveEnginesAtom = atom([
  new CogEngine({ _analysisType: "TokioDRCognitive" }),
  new Co1Engine({ _analysisType: "TokioDRCognitive" }),
]);

export const tateshinaEnginesAtom = atom([new Mi1Engine(), new MciEngine()]);

export type QuestionnaireAnswers = {
  state: "idle" | "skipped" | "responded";
  name: string;
  answers: Record<string, string>;
};
export const questionnaireAtom = atom<QuestionnaireAnswers>({
  state: "idle",
  name: "",
  answers: {},
});

// 利用規約の同意状態を管理するAtom
export const termsAgreementAtom = atom<boolean>(false);

export type AccessTokenInfo = {
  access_token: string;
  expired_at: string;
};
const accessTokenStorage = createJSONStorage<AccessTokenInfo | null>(
  () => sessionStorage
);
export const accessTokenInfoAtom = atomWithStorage<AccessTokenInfo | null>(
  TOKEN_INFO_SESSION_STORAGE_KEY,
  null,
  accessTokenStorage
);

export const userIdAtom = atom<string>((get) => {
  const tokenInfo = get(accessTokenInfoAtom);
  if (!tokenInfo?.access_token) return "";

  try {
    const payload = decodeJwt(tokenInfo.access_token);
    return payload.userId;
  } catch {
    return "";
  }
});

// 現在のユーザーの同意状況
export const currentUserAgreementAtom = atom(
  (get): boolean => {
    const userId = get(userIdAtom);
    const allAgreements = get(allAgreementsAtom);
    return allAgreements[userId] || false;
  },
  (get, set, newValue: boolean | null): void => {
    if (newValue === null) {
      set(allAgreementsAtom, {});
      return;
    }

    const userId = get(userIdAtom);
    if (!userId) return;

    const allAgreements = get(allAgreementsAtom);
    set(allAgreementsAtom, {
      ...allAgreements,
      [userId]: newValue,
    });
  }
);

export const audioRecorder = new AudioRecorder(workletUrl);

export type EmployeeInfoType = {
  company_id: string;
  employee_name: string;
  employee_id: string;
};

export const employeeInfoInitial = {
  company_id: "",
  employee_name: "",
  employee_id: "",
};

export const employeeInfoAtom = atom<EmployeeInfoType>(employeeInfoInitial);
const passwordFlagStorage = createJSONStorage<boolean | null>(
  () => sessionStorage
);
export const passwordFlagAtom = atomWithStorage<boolean | null>(
  "password_flag",
  null,
  passwordFlagStorage
);

const engineTypesStorage = createJSONStorage<object>(() => sessionStorage);
export const engineTypesAtom = atomWithStorage<object>(
  "engineTypes",
  [],
  engineTypesStorage
);
const allEngineEnabledStorage = createJSONStorage<boolean>(
  () => sessionStorage
);
export const allEngineEnabledAtom = atomWithStorage<boolean>(
  "allEngineEnable",
  false,
  allEngineEnabledStorage
);

export const co2ClassificationThresholdLow = atom<number>(30);
export const co2ClassificationThresholdHigh = atom<number>(70);

// 録音パターン（1:50から7を引くパターン 2:100から7を引くパターン）
export const recordingPatternAtom = atom(0);

// 音声ファイル
export const sound02Atom = atom(new Audio());
